import clsx from 'clsx';
import { components, type OptionProps } from 'react-select';

import Checkbox, { CheckboxState } from 'components/atoms/Checkbox';
import type { BaseOptionType, SelectAllOptionType } from '../../types';

type OptionWithOptionalProps = BaseOptionType & {
  isCustom?: boolean;
};

type EnhancedSelectProps = {
  selectAllOption?: SelectAllOptionType;
};

type EnhancedOptionProps<
  OT extends BaseOptionType,
  M extends boolean,
> = OptionProps<OT, M> & {
  selectProps: OptionProps<OT, M>['selectProps'] & EnhancedSelectProps;
};

const CheckboxOption = <
  OT extends BaseOptionType = BaseOptionType,
  M extends boolean = boolean,
>(
  props: EnhancedOptionProps<OT, M>,
) => {
  const { isSelected, data, selectProps } = props;

  const selectAllOption = selectProps?.selectAllOption;
  const selectAllValue = selectAllOption?.value || '';
  const isSelectAll = data.value === selectAllValue;
  const isDisabled = data.isDisabled || false;

  // For the select all option, we need to determine its state based on other selections
  let state: CheckboxState;

  if (isSelectAll) {
    // This is the select all option
    // Get all regular options (excluding the select all option)
    const allOptions = (selectProps?.options || []) as OT[];
    const regularOptions = allOptions.filter(
      opt => opt.value !== selectAllValue,
    );

    // Get current selected values (excluding select all)
    const selectedValues = Array.isArray(selectProps?.value)
      ? selectProps?.value
      : [];
    const selectedRegularOptions = selectedValues.filter(
      val => val.value !== selectAllValue,
    );

    if (selectedRegularOptions.length === 0) {
      state = CheckboxState.UNCHECKED;
    } else if (selectedRegularOptions.length === regularOptions.length) {
      state = CheckboxState.CHECKED;
    } else {
      state = CheckboxState.INDETERMINATE;
    }
  } else {
    // This is a regular option
    state = isSelected ? CheckboxState.CHECKED : CheckboxState.UNCHECKED;
  }

  // Get the hint text - either from data.hint or from selectAllOption.hint
  const hintText = isSelectAll
    ? selectAllOption?.hint
    : 'hint' in data
      ? (data as OptionWithOptionalProps).hint
      : undefined;

  return (
    <components.Option {...props}>
      <div
        className={clsx('flex items-center gap-xs2', {
          'text-helper': isDisabled,
        })}
      >
        <Checkbox state={state} />
        <div className="flex items-center gap-xs1">
          <span className="text-primary text-body-regular font-regular">
            {data.label}
          </span>
          {hintText && (
            <span className="text-helper text-body-regular">{hintText}</span>
          )}
          {'isCustom' in data && (data as OptionWithOptionalProps).isCustom && (
            <span className="text-helper text-body-regular">
              Custom metrics
            </span>
          )}
        </div>
      </div>
    </components.Option>
  );
};

export default CheckboxOption;
